// The words of the measure of the sends of Guestlist (ADR-313, ADR-314), plain tuples: @nexxt/db builds the CHECKs of
// the tables of crm from them (ADR-271), crm-beacon and crm the signals, the screens their filters.

/** What a signal is judged to be (ADR-313 §6): every screen shows the human count and the raw one. */
export const engagementVerdicts = ["human", "bot", "mpp", "proxy", "unknown", "system"] as const;
export type EngagementVerdict = (typeof engagementVerdicts)[number];

/** The verdicts the edge gives (`classify.rules.ts` of crm-beacon): `system` is only for what a provider says. */
export const edgeVerdicts = ["human", "bot", "mpp", "proxy", "unknown"] as const;
export type EdgeVerdict = (typeof edgeVerdicts)[number];

/**
 * The kinds of `crm.engagement_event`: what crm-beacon and the providers say about an email, and what crm derives from
 * the funnel of a personal link of booking (`linkFunnelEvents`).
 */
export const engagementKinds = [
  "click",
  "open",
  "open_aggregate",
  "honeypot",
  "unsubscribe",
  "delivered",
  "bounce",
  "complaint",
  "reject",
  "delay",
  "link_opened",
  "slots_viewed",
  "booked",
] as const;
export type EngagementKind = (typeof engagementKinds)[number];

/** Who said it: the edge, the provider of the email, or booking for the funnel of its link. */
export const engagementSources = ["beacon", "provider", "booking"] as const;
export type EngagementSource = (typeof engagementSources)[number];

/**
 * How much a send measures of one recipient, decided when the send is prepared (ADR-314 §1): `full` with the consent to
 * opens, `links` by default (clicks by person, the aggregate pixel), `aggregate` for who objected (clicks without a
 * person too).
 */
export const trackingLevels = ["full", "links", "aggregate"] as const;
export type TrackingLevel = (typeof trackingLevels)[number];

/**
 * What Guestlist measures of a contact, as the indicator "What we measure" says it (ADR-314 §5): opens and clicks with
 * the consent to opens, clicks by default, counted without a name after an objection to clicks, nothing after a no to
 * every email.
 */
export const contactMeasures = ["opens_and_clicks", "clicks", "anonymous", "none"] as const;
export type ContactMeasure = (typeof contactMeasures)[number];

/** Where the email of a recipient is (ADR-313 §6): a monotone machine, `bounced`, `rejected` and `complained` final. */
export const deliveryStates = [
  "pending",
  "sent",
  "delayed",
  "soft_bounced",
  "delivered",
  "bounced",
  "rejected",
  "complained",
] as const;
export type DeliveryState = (typeof deliveryStates)[number];

/**
 * What a send is: an email of a campaign, the personal booking links (P8.14i-a1), or the question after a visit to the
 * showroom (P8.14v-m, ADR-324 §1); a "no" may name one.
 */
export const sendKinds = ["campaign", "booking_links", "feedback"] as const;
export type SendKind = (typeof sendKinds)[number];

/** The three answers of the question after a visit (ADR-324 §1), one tap each, from the best. */
export const feedbackAnswers = ["great", "good", "poor"] as const;
export type FeedbackAnswer = (typeof feedbackAnswers)[number];

/**
 * Where a recipient of a send with an A/B test falls (ADR-324 §2): one of the two variants of the test, or the rest,
 * who receive the winner.
 */
export const sendVariants = ["a", "b", "rest"] as const;
export type SendVariant = (typeof sendVariants)[number];

/**
 * The life of a send (ADR-319): a draft, asked to be approved (by a tool too), approved by a person in three steps, then
 * its Workflow sends it; a person or the breaker pauses it, a person resumes or cancels it.
 */
export const sendStatuses = [
  "draft",
  "awaiting_approval",
  "approved",
  "sending",
  "paused",
  "sent",
  "cancelled",
] as const;
export type SendStatus = (typeof sendStatuses)[number];

/** Why a send is paused (ADR-319): a person, the breaker on the bounces and complaints, or a step that kept failing. */
export const sendPauseReasons = ["person", "breaker", "error"] as const;
export type SendPauseReason = (typeof sendPauseReasons)[number];

/**
 * Why a recipient of a send gets no email (ADR-319, ADR-314): checked when the send is prepared and again by each batch.
 * `no_consent` is a prospect without a consent to the emails (B-141, on its assumption); `no_booking_link` a contact
 * booking gives no link.
 */
export const sendSkipReasons = [
  "withdrawn",
  "opted_out",
  "suppressed",
  "no_email",
  "no_consent",
  "no_booking_link",
  "cancelled",
  "interrupted",
] as const;
export type SendSkipReason = (typeof sendSkipReasons)[number];

/** What a link of a send is, for the click map (ADR-313 §3): `booking` has no address, it never goes through the redirect. */
export const sendLinkKinds = [
  "booking",
  "portfolio",
  "flyer",
  "product",
  "logo",
  "button",
  "text",
  "image",
  "event",
  "unsubscribe",
  "preferences",
  "honeypot",
  "feedback",
] as const;
export type SendLinkKind = (typeof sendLinkKinds)[number];

/**
 * The documents of a campaign that Nexxt shows in its own viewer (ADR-317): the Portfolio and the Flyer of ADR-312,
 * uploaded as a PDF in Guestlist and turned into pictures of their pages.
 */
export const campaignDocumentKinds = ["portfolio", "flyer"] as const;
export type CampaignDocumentKind = (typeof campaignDocumentKinds)[number];

/** A document while its pages are uploaded, then shown, then put away: never deleted (the emails sent name it). */
export const campaignDocumentStatuses = ["draft", "ready", "archived"] as const;
export type CampaignDocumentStatus = (typeof campaignDocumentStatuses)[number];

/** The events a provider of the email sends back (SES, Brevo). */
export const providerEvents = ["delivered", "bounce", "complaint", "reject", "delay"] as const;
export type ProviderEvent = (typeof providerEvents)[number];

export const bounceTypes = ["hard", "soft"] as const;
export type BounceType = (typeof bounceTypes)[number];

/** What a "no" covers (ADR-314 §4): every email of Tomorrow, the emails of the brand of the send, or of its kind. */
export const unsubscribeScopes = ["all", "brand", "campaign_type"] as const;
export type UnsubscribeScope = (typeof unsubscribeScopes)[number];

/** How it came: the button of the client of mail (RFC 8058), the page of the preferences, a reply to the mailto. */
export const unsubscribeVias = ["one_click", "preference_center", "mailto"] as const;
export type UnsubscribeVia = (typeof unsubscribeVias)[number];

/** The three choices of a contact (ADR-314 §2): the emails, the opens measured, the clicks recorded by person. */
export const consentPurposes = ["marketing", "open_tracking", "link_tracking"] as const;
export type ConsentPurpose = (typeof consentPurposes)[number];

/** The states of the choices: `granted` and `withdrawn`; `unset` is an open measure never asked; `objected` a click. */
export const consentStates = ["granted", "withdrawn", "unset", "objected"] as const;
export type ConsentState = (typeof consentStates)[number];

/** Where a choice was given or taken away (ADR-314 §2): the proof in `crm.consent_event`. */
export const consentChannels = [
  "one_click",
  "mailto",
  "preference_center",
  "staff",
  "complaint",
  "import",
  "booking_page",
] as const;
export type ConsentChannel = (typeof consentChannels)[number];

/** Why an address receives nothing more (ADR-314 §2), kept after an erasure to honour the objection. */
export const suppressionReasons = ["hard_bounce", "complaint", "unsubscribe"] as const;
export type SuppressionReason = (typeof suppressionReasons)[number];

/** Why a signal did not become an event (`crm.signal_drop`): the health of the measure, by day. */
export const signalDropReasons = [
  "invalid",
  "bad_token",
  "unknown_send",
  "unknown_recipient",
  "unknown_message",
  "unknown_link",
  "out_of_window",
  "parked",
  // the time on the pages of a document of a campaign (ADR-317): a document or a page crm does not have, a beat that is
  // not a person's
  "unknown_document",
  "unknown_page",
  "not_human",
] as const;
export type SignalDropReason = (typeof signalDropReasons)[number];

/** The class of the user agent the edge tells (never the user agent itself, ADR-313 §10). */
export const uaClasses = [
  "browser",
  "mail_client",
  "proxy",
  "preview",
  "crawler",
  "library",
  "empty",
  "other",
] as const;

/**
 * What crm tells a person of Tomorrow in a toast (ADR-316 §5, phase 1 of the bell): a human signal of an account they
 * follow as its sales rep (a click, their booking link opened, its times seen, a booking), a send they approved paused
 * by the breaker, and the one summary of a day past the cap.
 */
export const crmNoticeKinds = ["clicked", "link_opened", "slots_viewed", "booked", "send_paused", "summary"] as const;
export type CrmNoticeKind = (typeof crmNoticeKinds)[number];

/** The moves whose card hands the seller the personal link of the person (ADR-316 §4, road A): once per campaign. */
export const crmLinkMoveKinds = ["recover", "offer_freed"] as const;
export type CrmLinkMoveKind = (typeof crmLinkMoveKinds)[number];
